import { IonIcon } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { api } from "@/lib/backend";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { Capacitor } from "@/lib/capacitor";
import { ActionSheet } from "@/lib/capacitor/action-sheet";
import { App } from "@/lib/capacitor/app";
import { Camera } from "@/lib/capacitor/camera";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { cameraIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { ensureMediaPermission } from "@/lib/permissions/media-permission";
import { queryClient } from "@/lib/providers/query";
import { queryOptions_signed_key_user } from "@/lib/query/file/file.get-signed-key";
import { cx } from "@/lib/style/cva.config";
import { useActionSheet } from "@/lib/use-action-sheet";
import { logger } from "@/logger";

interface EditableAvatarProps {
  userId: string;
  imageKey: string;
  className?: string;
}

const TOAST_KEY = "update-profile-picture";

const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

/** Groot genoeg voor een scherm met dubbele pixeldichtheid, klein genoeg om over 4G te uploaden. */
const MAX_IMAGE_WIDTH = 1024;

/**
 * De camera-plugin gooit ook wanneer de gebruiker de picker gewoon wegklikt. Dat is geen fout om
 * over te klagen, dus die filteren we eruit; de plugin geeft er geen code bij, alleen een tekst
 * ("User cancelled photos app").
 */
function isCancellation(error: unknown) {
  return error instanceof Error && /cancel/i.test(error.message);
}

export function EditableAvatar({ userId, imageKey, className }: EditableAvatarProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const PreferredHaptics = usePreferredHaptics();
  const [presentActionSheet] = useActionSheet();
  const [imageError, setImageError] = useState(false);

  // De foto die je zojuist koos, als blob-URL. Die tonen we meteen: wachten op de
  // upload, op een verse `me`, op een nieuwe ondertekende URL én op het downloaden
  // van je eigen foto duurt op een telefoon lang genoeg om te denken dat het niet
  // gelukt is.
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  // Zodat één mislukte load niet twee keer een refetch aanzwengelt.
  const retriedUrlRef = useRef<string | null>(null);

  const { data: signedUrl } = useQuery({
    ...queryOptions_signed_key_user([userId, imageKey]),
    enabled: !!imageKey && !!userId && imageKey.trim() !== "",
  });

  useEffect(() => {
    setImageError(false);
  }, [signedUrl?.url, imageKey]);

  // De blob-URL blijft anders aan het document hangen, ook nadat we hem vervangen.
  useEffect(() => {
    if (!localPreview) {
      return;
    }

    return () => URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const uploadMutation = useExtractedMutation({
    mutationKey: ["me", "profile-picture"],
    mutationFn: async (file: File) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";

      if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
        throw new Error(t("actions:update-profile-picture.invalid-type"));
      }

      return api.me.updateProfilePicture({ file });
    },
    onMutate: () => {
      // Bewust geen loading-toast: die laat op iOS een witte laag achter, zie
      // docs/common-issues.md.
      toast.dismiss(TOAST_KEY);
    },
    onSuccess: (data) => {
      toast.dismiss(TOAST_KEY);
      toast.success(t("actions:update-profile-picture.success"), { id: TOAST_KEY });
      setImageError(false);

      try {
        // Samenvoegen en niet overschrijven. Dit was `setQueryData(["me", {}], data)`, en
        // daarmee hing je hele profiel aan wat dit ene antwoord toevallig teruggeeft: een
        // veld dat er niet in staat verdween uit de cache. Voor `image` is dat precies het
        // verschil tussen een foto en een leeg silhouet -- en omdat de sleutel dan leeg is,
        // staat ook de query naar de ondertekende URL uit en valt er niets meer te herstellen
        // tot `me` opnieuw wordt opgehaald.
        queryClient.setQueryData(["me", {}], (previous: unknown) =>
          previous && typeof previous === "object" ? { ...previous, ...data } : data,
        );

        // En daarna alsnog de echte `me` ophalen. Dit stond hier niet omdat een invalidatie
        // de instellingenpagina opnieuw liet renderen en op iOS ion-page-invisible liet
        // plakken -- maar dat kwam door de router die zichzelf hermontte, en dat is bij de
        // wortel opgelost (zie docs/common-issues.md). `me` houdt bovendien zijn vorige data
        // vast tijdens het verversen, dus er is geen moment waarop het profiel weg is.
        void queryClient.invalidateQueries({ queryKey: ["me"] });

        // Alleen de ondertekende URL opnieuw ophalen; cache-busting op de URL zelf
        // breekt de handtekening.
        void queryClient.invalidateQueries({ queryKey: ["file", "user", userId] });
      } catch (error) {
        logger.error("Failed to update cache after profile picture update:", error);
      }
    },
    onError: (error) => {
      toast.dismiss(TOAST_KEY);
      toast.error(t("actions:update-profile-picture.error"), { id: TOAST_KEY });
      // Anders blijf je naar een foto kijken die nooit is opgeslagen.
      setLocalPreview(null);
      logger.error(error);
    },
  });

  const upload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("actions:update-profile-picture.file-too-large"));
      return;
    }

    setLocalPreview(URL.createObjectURL(file));

    try {
      await uploadMutation.mutateAsync(file);
    } catch {
      // Afgehandeld in onError.
    }
  };

  const captureAndUpload = async (source: "camera" | "photos") => {
    try {
      // Een geweigerde toestemming vraagt het systeem niet nog eens; de knop naar de instellingen
      // is dan het enige dat de gebruiker nog verder helpt. Zonder die knop stond hier een melding
      // die zei wat er niet mocht en niet waar je het aanzet.
      if ((await ensureMediaPermission(source)) === "blocked") {
        toast.error(t(`actions:update-profile-picture.${source}-denied`), {
          action: {
            label: t("common:open-settings"),
            onClick: () => void App.openSettings(),
          },
        });
        return;
      }

      const photo = await Camera.getPhoto({
        quality: 80,
        width: MAX_IMAGE_WIDTH,
        correctOrientation: true,
        resultType: Camera.ResultType.Uri,
        source: source === "camera" ? Camera.Source.Camera : Camera.Source.Photos,
      });

      if (!photo.webPath) {
        return;
      }

      const blob = await fetch(photo.webPath).then((response) => response.blob());
      const format = photo.format || "jpeg";

      await upload(
        new File([blob], `profile-picture.${format}`, { type: blob.type || `image/${format}` }),
      );
    } catch (error) {
      if (isCancellation(error)) {
        return;
      }

      logger.error(error);
      toast.error(t("actions:update-profile-picture.error"));
    }
  };

  const handleClick = () => {
    PreferredHaptics.impact();

    // In de browser is er geen camera om naartoe te sturen; daar blijft de
    // bestandskiezer het enige dat werkt.
    if (!Capacitor.isNativePlatform()) {
      fileInputRef.current?.click();
      return;
    }

    void presentActionSheet({
      title: t("actions:update-profile-picture.action"),
      actions: [
        {
          text: t("actions:update-profile-picture.take-photo"),
          handler: () => void captureAndUpload("camera"),
        },
        {
          text: t("actions:update-profile-picture.choose-photo"),
          handler: () => void captureAndUpload("photos"),
        },
        { text: t("common:cancel"), style: ActionSheet.ButtonStyle.Cancel },
      ],
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      await upload(file);
    }

    event.target.value = "";
  };

  const handleImageError = () => {
    const failedUrl = signedUrl?.url;
    logger.error("Image failed to load:", failedUrl);

    // Meteen terugvallen. Een `img` met een kapotte src rendert als niets, dus wachten op de
    // uitkomst van de poging hieronder betekent naar een gat kijken -- en als de verse URL
    // gelijk is aan de kapotte verandert er niets meer, waardoor dat gat blijft staan.
    setImageError(true);

    // En dan één keer een verse ondertekende URL proberen; die verlopen. Komt er een andere
    // terug, dan zet het effect hierboven de fout weer uit en laadt hij alsnog.
    if (failedUrl && retriedUrlRef.current !== failedUrl) {
      retriedUrlRef.current = failedUrl;
      void queryClient.invalidateQueries({ queryKey: ["file", "user", userId] });
    }
  };

  const imageSource = useMemo(() => {
    if (localPreview) {
      return localPreview;
    }

    if (signedUrl?.url && !imageError) {
      return signedUrl.url;
    }

    // Bewust een lokaal silhouet en geen avatar-dienst: die verzon initialen uit
    // het gebruikers-id (een uuid, dus twee willekeurige tekens) en had er ook nog
    // een netwerkverbinding voor nodig op het moment dat er juist geen foto is.
    return EmptyAvatar;
  }, [localPreview, signedUrl?.url, imageError]);

  return (
    <div className={cx("relative size-[120px] rounded-full", className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        aria-label={t("actions:update-profile-picture.action")}
      />

      <button
        type="button"
        onClick={handleClick}
        className="relative size-[120px] overflow-hidden rounded-full focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        aria-label={t("actions:update-profile-picture.action")}
      >
        <img
          key={imageSource}
          src={imageSource}
          className="size-full rounded-full object-cover"
          alt=""
          onError={handleImageError}
          referrerPolicy="no-referrer"
        />
      </button>

      {/* Edit button overlay */}
      <button
        type="button"
        onClick={handleClick}
        tabIndex={-1}
        aria-hidden="true"
        className={cx(
          "absolute left-[91px] top-0 flex size-[33.333px] items-center justify-center rounded-full border-[0.778px] border-bordercolor-primary bg-white p-[5.25px] shadow-sm",
          "hover:bg-surface-primary active:bg-neutral-50",
          "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
        )}
      >
        <IonIcon icon={cameraIcon} className="size-[16.667px] text-txt-secondary" />
      </button>
    </div>
  );
}
